import React, { Fragment, useEffect, useRef, useState } from "react";
import "./testResult.scss";
import { useSelector } from "react-redux";
import { Editor } from "../../../utils/editor";
import {
  getQuestionTypes,
  getQuestionLevels,
  resetOptions,
  setOptionValues,
  setRightAnswer,
  setErrors,
  savedQuestion,
  editedQuestion,
  setQuestionValues,
  resetQuestion,
  uploadCodingQuestionFile,
  clearCreateQuestionState,
  getQuestionByID,
  resetQuestionById,
} from "../../../Redux/ActionCreators/testLibraryAction";
import {
  getSkills,
  getSkillTopics,
  emptyTopics,
} from "../../../Redux/ActionCreators/myTestsAction";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import JoditEditor from "jodit-react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CreatableSelect from "react-select/creatable";
import Select, { components } from "react-select";
import { useTranslation } from "react-i18next";
import CreateCodingQuestion from "../../CreateCodingQuestion/createCodingQuestion";
import CreateCodingQuestionv2 from "../../CreateCodingQuestion/createCodingQuestionv2";
import { Dialog } from "@mui/material";

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <img src="" alt="" />
    </components.DropdownIndicator>
  );
};

const ALLOWED_FILE_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "zip",
  "txt",
  "ppt",
  "pptx",
  "xls",
  "xlsx",
  "csv",
];

export default function CreateQuestion() {
  const [quesLevel, setQuesLevel] = useState(1);
  const [quesType, setQuesType] = useState(1);
  const [title, setTitle] = useState("");
  const [questionTitleError, setQuestionTitleError] = useState("");
  const [successMessageText, setsuccessMessageText] = useState("");
  const [successMessage, setSuccessMessage] = useState(false);
  const [skill, setSkill] = useState(null);
  const [topic, setTopic] = useState(null);
  const [duration, setDuration] = useState(1);
  const [selectType, setSelectType] = useState("score");
  const [score, setScore] = useState(1);
  const [suggestedAnswer, setSuggestedAnswer] = useState("");
  const [addComments, setAddComments] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileUploading, setFileUploading] = useState(false);
  const [questionFieldsHydrated, setQuestionFieldsHydrated] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [optionsVersion, setOptionsVersion] = useState(0);

  const { t } = useTranslation();
  const editorRef = React.useRef();
  const questionHydratedRef = useRef(false);
  const { questionType, questionLevel, qTitle, questionOptions, questionByIdResponse, loadingQuestionById } = useSelector(
    (state) => state.testLibrary,
  );
  const { skills, topics } = useSelector((state) => state.myTests);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stateKey = params.get('stateKey');
    if (stateKey && !location.state) {
      const stored = sessionStorage.getItem(stateKey);
      if (stored) {
        sessionStorage.removeItem(stateKey);
        navigate(location.pathname, { state: JSON.parse(stored), replace: true });
      }
    }
  }, []);

  const isCreateMode = !location.state?.edit && !location.state?.view;
  const isViewMode = location.state?.view === true;

  const codingVersion =
    location.state?.detail?.version === 1
      ? 1
      : location.state?.detail?.version === 2
        ? 2
        : 2;

  useEffect(() => {
    const firstRender = async () => {
      await dispatch(resetQuestion());

      const initialType = location.state.edit ? Number(location.state.detail?.questionTypeID) : 1;

      // Coding types are handled by embedded components — set type and return early
      if (location.state.edit && [7, 12, 13].includes(initialType)) {
        setQuesType(initialType);
        setInitialLoading(false);
        return;
      }

      await Promise.all([
        dispatch(getSkills()),
        dispatch(getQuestionTypes()),
        dispatch(getQuestionLevels()),
      ]);

      if ((location.state?.edit || location.state?.view) && location.state?.detail?.questionID) {
        await dispatch(resetQuestionById());
        setTimeout(() => {
          dispatch(getQuestionByID(location.state.detail.questionID));
        }, 0);
      }

      setInitialLoading(false);
    };

    firstRender();

    return () => {
      if (location.state?.detail) {
        dispatch(setQuestionValues(location.state.detail));
      }
    };
  }, []);

  useEffect(() => {
    if (loadingQuestionById || !questionByIdResponse) return;
    if (questionHydratedRef.current) return;

    const detail = questionByIdResponse?.question || questionByIdResponse;
    if (!detail) return;

    // Reject stale Redux data from a previously viewed question.
    // Without this guard, the effect runs with the old questionByIdResponse before
    // firstRender's resetQuestionById() clears it, locks the ref, and then ignores
    // the correct data when it arrives.
    const expectedId = location.state?.detail?.questionID;
    if (expectedId && detail.questionID !== expectedId) return;

    questionHydratedRef.current = true;

    dispatch(setQuestionValues(detail));
    setQuesType(detail?.questionTypeID);
    setQuesLevel(detail?.questionLevelId);
    setSelectType(detail?.score === 0 ? "na" : "score");

    if (Number(detail?.questionTypeID) === 5) {
      setScore(detail?.score && detail?.score > 0 ? detail?.score : 1);
    } else if (Number(detail?.questionTypeID) === 11) {
      const nonEmptyOptions = (detail?.questionOptions || []).filter(
        (opt) => opt.questionOption?.trim() !== "",
      ).length;
      setScore(nonEmptyOptions > 0 ? nonEmptyOptions : 1);
    } else {
      setScore(detail?.score ?? 1);
    }

    setAddComments(detail?.shouldProvideReason === 1);
    setSuggestedAnswer(detail?.suggestedAnswer || "");

    if (detail?.skillName === null) {
      setSkill("");
    } else {
      setSkill({ label: detail.skillName, value: detail?.skillID });
      if (detail?.skillID && detail.skillID > 0) {
        dispatch(getSkillTopics(0, detail.skillID));
      }
    }

    if (detail?.questionTypeID === 14 && detail?.data) {
      try {
        const parsed = JSON.parse(detail.data);
        if (parsed?.files?.length) {
          setUploadedFiles(parsed.files);
        }
      } catch (e) {
        console.error("Invalid file data");
      }
    }

    detail?.skillTopicName === null
      ? setTopic("")
      : setTopic({
          label: detail.skillTopicName,
          value: detail?.skillTopicId,
        });

    setDuration(detail.duration);

    if (Number(detail?.questionTypeID) === 11) {
      const opts = detail?.questionOptions || [];
      if (opts.length < 5) {
        const missing = 5 - opts.length;
        const extra = Array.from({ length: missing }, (_, idx) => ({
          index: opts.length + idx,
          questionOption: "",
          iscorrect: false,
          optionError: "",
        }));
        dispatch(
          setQuestionValues({
            ...detail,
            questionOptions: [...opts, ...extra],
          }),
        );
      }
    }
    setQuestionFieldsHydrated(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionByIdResponse, loadingQuestionById]);

  // The Editor child component calls dispatch(resetQuestion()) on mount, which clears questionOptions.
  // This effect runs AFTER the Editor's useEffect (parent runs after child) and restores the options.
  // optionsVersion is then incremented to force JoditEditor instances to remount with the restored values.
  useEffect(() => {
    if (!questionFieldsHydrated || !questionByIdResponse) return;
    const detail = questionByIdResponse?.question || questionByIdResponse;
    if (!detail) return;
    dispatch(setQuestionValues(detail));
    setOptionsVersion((v) => v + 1);
  }, [questionFieldsHydrated]);

  useEffect(() => {
    if (quesType === 11 && !location.state?.edit) {
      const defaults = [
        "Strongly Disagree",
        "Disagree",
        "Neutral",
        "Agree",
        "Strongly Agree",
      ];

      const populated = questionOptions.map((opt, i) => ({
        ...opt,
        questionOption: opt.questionOption?.trim()
          ? opt.questionOption
          : defaults[i], // ✅ prefill only once
      }));

      dispatch(
        setQuestionValues({
          ...location.state.detail,
          questionOptions: populated,
        }),
      );

      // set initial score = number of non-empty
      const nonEmpty = populated.filter(
        (opt) => opt.questionOption.trim() !== "",
      ).length;
      setScore(nonEmpty > 0 ? nonEmpty : 1);
    }
  }, [quesType]);

  const handleSkill = async (e) => {
    await setSkill(e);
    setTopic(null);
    if (!e.__isNew__) {
      await dispatch(getSkillTopics(0, e.value));
    } else {
      await dispatch(emptyTopics());
    }
  };

  const handleTopic = (e) => {
    setTopic(e);
  };

  const changeQuestionType = (e) => {
    const type = Number(e.target.value);
    setQuesType(type);
    dispatch(resetOptions());
    setSkill(null);
    setTopic(null);
    dispatch(emptyTopics());

    dispatch(getSkills());

    if (type !== 14) {
      setUploadedFiles([]);
    }
  };

  const changeQuestionLevel = (e) => {
    setQuesLevel(e.target.value);
  };

  const onChangeOption = (key, idx, value) => {
    dispatch(setOptionValues(key, idx, value));

    if (quesType === 11) {
      const updatedOptions = questionOptions.map((opt, i) =>
        i === idx ? { ...opt, questionOption: value } : opt,
      );

      const nonEmptyOptions = updatedOptions.filter(
        (opt) => opt.questionOption.trim() !== "",
      ).length;

      setScore(nonEmptyOptions > 0 ? nonEmptyOptions : 1);
    }
  };

  const handleOptionChange = (e) => {
    setSelectType(e.target.value);
    setScore(e.target.value === "na" ? 0 : 1);
  };

  const saveQuestion = async () => {
    setSaveLoading(true);

    const isOptionEmpty = (value) => {
      if (!value) return true;

      const textOnly = value
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, "")
        .trim();

      return textOnly === "";
    };

    try {
      if (!skill) {
        setSaveLoading(false);
        setSuccessMessage(true);
        setsuccessMessageText(t("Please select at least one Knowledge area"));
        return;
      }
      if (!topic) {
        setSaveLoading(false);
        setSuccessMessage(true);
        setsuccessMessageText(t("Please select at least one Topic"));
        return;
      }
      if (!duration) {
        setSaveLoading(false);
        setSuccessMessage(true);
        setsuccessMessageText(t("Please enter duration"));
        return;
      }

      if (
        [1, 5, 11, 14].includes(quesType) &&
        (!qTitle || qTitle.trim() === "")
      ) {
        setQuestionTitleError(t("Question title can't be empty"));
        setSaveLoading(false);
        return;
      }

      if (quesType === 5) {
        if (!score && score !== 0) {
          setSuccessMessage(true);
          setsuccessMessageText(t("Please enter score"));
          setSaveLoading(false);
          return;
        }

        if (!suggestedAnswer || suggestedAnswer.trim() === "") {
          setSuccessMessage(true);
          setsuccessMessageText(t("Suggested Answer is required"));
          setSaveLoading(false);
          return;
        }
      }

      if (quesType === 1) {
        const filledOptions = questionOptions.filter(
          (opt) => !isOptionEmpty(opt.questionOption),
        );

        if (filledOptions.length < 2) {
          setSuccessMessage(true);
          setsuccessMessageText(
            t("MCQ questions require at least two filled options"),
          );
          setSaveLoading(false);
          return;
        }

        const hasCorrect = filledOptions.some((opt) => opt.iscorrect);
        if (!hasCorrect) {
          setSuccessMessage(true);
          setsuccessMessageText(t("Please select at least one correct option"));
          setSaveLoading(false);
          return;
        }
      }

      if (quesType === 11) {
        if (isOptionEmpty(qTitle)) {
          setQuestionTitleError(t("Question title can't be empty"));
          setSaveLoading(false);
          return;
        }
      }

      if (quesType === 14) {
        if (!qTitle || qTitle.trim() === "") {
          setQuestionTitleError(t("Question title can't be empty"));
          setSaveLoading(false);
          return;
        }

        if (!uploadedFiles || uploadedFiles.length === 0) {
          setSuccessMessage(true);
          setsuccessMessageText(t("Please upload a file"));
          setSaveLoading(false);
          return;
        }
      }

      let base = {
        question: qTitle,
        questionTypeID: quesType,
        questionLevelId: quesLevel,
        questionTag: "",
        isPreQualifierQuestions: true,
        skillId: skill?.value === skill?.label ? 0 : skill?.value,
        skillName: skill?.label,
        skillTopicId: topic?.value === topic.label ? 0 : topic?.value,
        skillTopicName: topic?.label,
        duration: duration,
        shouldProvideReason: addComments ? 1 : 0, // ✅ checkbox value
      };

      // Include questionID for edit case
      if (location.state.edit) {
        base.questionID = location.state.detail?.questionID;
      }

      let obj = {};

      if (quesType === 5) {
        // Essay
        obj = {
          ...base,
          score,
          suggestedAnswer,
          questionOptions: questionOptions?.map(
            (el) =>
              [
                {
                  questionID: el.questionID,
                  questionOptionID: el.questionOptionID,
                  questionOption: el.questionOption,
                  iscorrect: el.iscorrect,
                },
              ][0],
          ),
        };
      } else if (quesType === 1) {
        // MCQ
        obj = {
          ...base,
          score,
          questionOptions: questionOptions.slice(0, 4).map((el) => ({
            questionID: el.questionID,
            questionOptionID: el.questionOptionID,
            questionOption: el.questionOption,
            iscorrect: el.iscorrect,
            score: el.iscorrect ? 1 : 0, // ✅ correct → 1, else 0
          })),
        };
      } else if (quesType === 11) {
        // Rating
        obj = {
          ...base,
          score,
          questionOptions: questionOptions.slice(0, 5).map((el, i) => ({
            questionID: el.questionID,
            questionOptionID: el.questionOptionID,
            questionOption: el.questionOption,
            score: i + 1, // ✅ first → 1, second → 2, ...
          })),
        };
      } else if (quesType === 14) {
        obj = {
          ...base,
          score,
          questionOptions: [],
          data: JSON.stringify({
            files: uploadedFiles.map((file) => ({
              name: file.name,
              url: file.url,
            })),
          }),
        };
      } else {
        // Other types (default behavior)
        obj = {
          ...base,
          questionOptions: questionOptions.map((el) => ({
            questionID: el.questionID,
            questionOptionID: el.questionOptionID,
            questionOption: el.questionOption,
            iscorrect: el.iscorrect,
          })),
        };
      }
      // Save or update
      if (!location.state.edit) {
        const res = await dispatch(savedQuestion(obj));
        if (res?.type === "SAVED_QUESTION") {
          await dispatch(clearCreateQuestionState());
          location?.state?.question
            ? navigate(-1)
            : navigate("/home/add-question", {
              state: {
                isQuestionVisible: true,
                testId: location.state.testId ? location.state.testId : "",
              },
            });
        }
      } else {
        const res = await dispatch(editedQuestion(obj));
        if (res?.type === "EDITED_QUESTION") {
          await dispatch(clearCreateQuestionState());
          location?.state?.question
            ? navigate(-1)
            : navigate("/home/add-question", {
              state: { isQuestionVisible: true },
            });
        }
      }
    } catch (error) {
      setSaveLoading(false);
    }
  };

  const AuthStr = "Bearer " + localStorage.getItem("loginToken");
  const config = {
    toolbarInlineForSelection: true,
    toolbar: true,
    tabIndex: 1,
    placeholder: "",
    readonly: isViewMode,
    statusbar: false,
    showCharsCounter: false,
    showWordsCounter: false,
    minHeight: 100,
    width: "100%",
    toolbarSticky: false,
    toolbarDisableStickyForMobile: false,
    allowResizeX: false,
    allowResizeY: false,
    uploader: {
      url: `${process.env.REACT_APP_API_KEY}admin/my-content/upload/image`,
      headers: { Authorization: AuthStr },
      filesVariableName: function (e) {
        return "image";
      },
      insertImageAsBase64URI: false,
      prepareData: function (data) {
        // data.append("image", );
        return data;
      },
      isSuccess: function (resp) {
        return resp;
      },
      process: function (resp) {
        return {
          files: resp.image,
          path: resp.image,
          baseurl: resp.image,
          error: resp.error,
          message: resp.message,
        };
      },

      defaultHandlerSuccess: function (data) {
        if (this.componentName === "jodit-uploader") {
          this.jodit.selection.insertImage(data.baseurl, null, 100);
        } else {
          this.selection.insertImage(data.baseurl, null, 100);
        }
      },
    },
    autoFocus: true,
    removeButtons: ["eraser", "fullsize"],
    askBeforePasteFromWord: false,
    askBeforePasteHTML: false,
    defaultActionOnPaste: "insert_clear_html",
    defaultActionOnPasteFromWord: "insert_as_html",
    enableDragAndDropFileToEditor: true,
    usePopupForSpecialCharacters: true,
    // cleanHTML: { removeEmptyElements: false, },
    sizeLG: 900,
    sizeMD: 700,
    sizeSM: 400,
    buttons: [
      "source",
      "|",
      "insertFormula",
      "bold",
      "italic",
      "underline",
      "|",
      "ul",
      "ol",
      "|",
      "font",
      "fontsize",
      "brush",
      "paragraph",
      "|",
      "image",
      "table",
      "link",
      "|",
      "left",
      "center",
      "right",
      "justify",
      "|",
      "undo",
      "redo",
      "|",
    ],
    buttonsMD: [
      "source",
      "|",
      "bold",
      "italic",
      "underline",
      "|",
      "ul",
      "ol",
      "|",
      "font",
      "fontsize",
      "brush",
      "paragraph",
      "|",
      "image",
      "table",
      "link",
      "|",
      "left",
      "center",
      "right",
      "justify",
      "|",
      "undo",
      "redo",
      "|",
    ],
    buttonsSM: [
      "bold",
      "italic",
      "underline",
      "|",
      "ul",
      "ol",
      "|",
      "fontsize",
      "brush",
      "superscript",
      "subscript",
      "paragraph",
      "|",
      "image",
      "table",
      "link",
      "|",
      "left",
      "center",
      "right",
      "|",
      "undo",
      "redo",
      "|",
    ],
    buttonsXS: [
      "bold",
      "italic",
      "underline",
      "|",
      "brush",
      "paragraph",
      "|",
      "image",
      "left",
      "center",
      "right",
      "|",
      "undo",
      "redo",
      "|",
      "eraser",
    ],
  };

  const snackClosed = () => {
    setSuccessMessage(false);
    setsuccessMessageText("");
  };

  const handleScoreChange = (e) => {
    const value = e.target.value;
    if (value === "") {
      setScore("");
      return;
    }

    setScore(Number(value));
    const numericValue = parseInt(value, 10);

    // Only update the state if the numeric value is between 1 and 5
    if (numericValue >= 1 && numericValue <= 5) {
      setScore(numericValue);
    } else {
      setSuccessMessage(true);
      setsuccessMessageText(t("Please enter score between 1 to 5"));
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";

    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();

    if (!ALLOWED_FILE_EXTENSIONS.includes(ext)) {
      setSuccessMessage(true);
      setsuccessMessageText("File format not allowed");
      return;
    }

    const tempFile = {
      name: file.name,
      url: "",
      isUploading: true,
    };

    setUploadedFiles((prev) => [...prev, tempFile]);

    try {
      const res = await dispatch(uploadCodingQuestionFile(file));

      if (res?.url) {
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f === tempFile ? { ...f, url: res.url, isUploading: false } : f,
          ),
        );
      }
    } catch (e) {
      setUploadedFiles((prev) => prev.filter((f) => f !== tempFile));
    }
  };

  const removeFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const downloadFile = async (url, fileName) => {
    try {
      const response = await fetch(url, {
        method: "GET",
      });

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("File download failed", error);
      setSuccessMessage(true);
      setsuccessMessageText("Unable to download file");
    }
  };

  const handleBack = async () => {
    dispatch(clearCreateQuestionState());
    navigate(-1);
  };

  const isCodingType = [7, 12, 13].includes(Number(location.state?.detail?.questionTypeID));
  const isPageLoading = initialLoading || ((location.state?.view || location.state?.edit) && !isCodingType && (loadingQuestionById || !questionFieldsHydrated));

  if (!location.state && new URLSearchParams(location.search).get('stateKey')) {
    return null;
  }

  if (isPageLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <div
          className="spinner-border"
          role="status"
          style={{ width: "3rem", height: "3rem", color: "#f75891" }}
        >
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        open={successMessage}
        onClose={snackClosed}
        autoHideDuration={4000}
      >
        <Alert onClose={snackClosed} severity="error" sx={{ width: "100%" }}>
          {successMessageText}
        </Alert>
      </Snackbar>
      <div
        className="bg-white create-question-main"
        style={{
          margin: "12px",
          padding: "12px",
          borderRadius: "8px",
          border: "1px solid #E5E7EB",
        }}
      >
        <div
          className="d-flex"
          style={{ paddingLeft: "40px", marginBottom: "20px" }}
        >
          <img
            src="/images/back.svg"
            alt=""
            className="cursor-pointer"
            onClick={handleBack}
          />
          &nbsp;&nbsp;
          <h1 className="maintxt">
            {" "}
            {location.state?.view
              ? t("View Question")
              : location.state?.edit
                ? t("Edit Question")
                : t("Create Question")}
          </h1>
        </div>

        <div className={window.innerWidth > 767 ? "row px-5" : "row px-2"}>
          <div className="col-12">
            <div className="row mb-1">
              <div className="col-md-6 col-12">
                <div className="row mt-4 mb-2">
                  <div className="col-12">
                    <label className="labeltext px-0">
                      {t("Question Type", { ns: "home" })}*
                    </label>
                    <br />
                  </div>
                </div>
                <select
                  className="form-select"
                  value={quesType}
                  onChange={(e) => changeQuestionType(e)}
                  disabled={isViewMode || location.state.edit}
                >
                  {questionType
                    ?.filter(
                      (el) =>
                        el.questionTypeID === 1 ||
                        el.questionTypeID === 5 ||
                        el.questionTypeID === 7 ||
                        el.questionTypeID === 11 ||
                        el.questionTypeID === 12 ||
                        el.questionTypeID === 13 ||
                        el.questionTypeID === 14,
                    )
                    ?.map((el) => (
                      <option key={el.questionTypeID} value={el.questionTypeID}>
                        {el.questionTypeName}
                      </option>
                    ))}
                </select>
              </div>
              {!(quesType === 7 || quesType === 12 || quesType === 13) && (
                <div className="col-md-6 col-12">
                  <div className="row mt-4 mb-2">
                    <div className="col-12">
                      <label className="labeltext px-0">
                        {t("Question Level", { ns: "home" })}*
                      </label>
                      <br />
                    </div>
                  </div>
                  <select
                    className="form-select"
                    value={quesLevel}
                    onChange={(e) => changeQuestionLevel(e)}
                    disabled={isViewMode || location.state.edit}
                  >
                    {questionLevel?.map((el) => (
                      <option
                        key={el.questionLevelID}
                        value={el.questionLevelID}
                      >
                        {el.questionLevelName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            {quesType === 7 ? (
              codingVersion === 1 ? (
                <CreateCodingQuestion
                  embedded
                  fromCreateQuestion
                  questionTypeID={quesType}
                  existingDetail={location.state?.detail || null}
                  editMode={location.state?.edit || false}
                  viewMode={location.state?.view || false}
                />
              ) : (
                <CreateCodingQuestionv2
                  embedded
                  fromCreateQuestion
                  questionTypeID={quesType}
                  existingDetail={location.state?.detail || null}
                  editMode={location.state?.edit || false}
                  viewMode={location.state?.view || false}
                />
              )
            ) : quesType === 12 || quesType === 13 ? (
              <CreateCodingQuestion
                embedded
                fromCreateQuestion
                questionTypeID={quesType}
                existingDetail={location.state?.detail || null}
                editMode={location.state?.edit || false}
                viewMode={location.state?.view || false}
              />
            ) : (
              <>
                <div className="row mb-1">
                  <div className="col-md-6 col-12">
                    <div className="row mt-4 mb-2">
                      <div className="col-12">
                        <label className="labeltext px-0">
                          {t("Knowledge Area", { ns: "home" })}*
                        </label>
                        <br />
                      </div>
                    </div>
                    <div className="mt-2">
                      <CreatableSelect
                        options={skills}
                        components={DropdownIndicator}
                        value={skill}
                        placeholder={t("Select Knowledge Area", { ns: "home" })}
                        onChange={(e) => handleSkill(e)}
                        isDisabled={isViewMode}
                      />
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <div className="row mt-4 mb-2">
                      <div className="col-12">
                        <label className="labeltext px-0">
                          {t("Topics/tags", { ns: "home" })}*
                        </label>
                        <br />
                      </div>
                    </div>
                    <div className="mt-2">
                      <CreatableSelect
                        components={DropdownIndicator}
                        options={topics}
                        value={topic}
                        placeholder={t("Select Topic/tags", { ns: "home" })}
                        onChange={(e) => handleTopic(e)}
                        isDisabled={(isCreateMode && !skill) || isViewMode}
                      />
                    </div>
                  </div>
                </div>
                <div className="row mt-4 mb-2">
                  <div className="col-12">
                    <label className="labeltext px-0">
                      {t("Duration (mins)", { ns: "home" })}*
                    </label>
                    <br />
                  </div>
                </div>
                <div className="row mt-2 mb-2">
                  <div className="col-md-6 col-12">
                    <input
                      type="number"
                      className="form-control"
                      value={duration}
                      minLength={0}
                      min={1}
                      // onChange={(e) => setDuration(e.target.value)}
                      onChange={(e) => {
                        const val = e.target.value;

                        // ✅ allow empty input
                        if (val === "") {
                          setDuration("");
                          return;
                        }

                        setDuration(Number(val));
                      }}
                      onBlur={() => {
                        // ✅ enforce minimum only on blur
                        if (duration === "" || duration <= 0) {
                          setDuration(1);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "-" || e.key === "e") {
                          e.preventDefault();
                        }
                      }}
                      disabled={isViewMode}
                      required
                    />
                  </div>
                </div>
                {quesType === 5 ||
                  quesType === 1 ||
                  quesType === 11 ||
                  quesType === 14 ? (
                  <Fragment>
                    <div className="row mt-4 mb-2">
                      <div className="col-12">
                        <label className="labeltext px-0">
                          {t("Score", { ns: "home" })}*
                        </label>
                        <br />
                      </div>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="radio2"
                        checked={selectType === "score" ? true : false}
                        id="score"
                        onChange={handleOptionChange}
                        value="score"
                        disabled={isViewMode}
                      />

                      <label className="form-check-label">
                        {t("Score", { ns: "home" })}
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="radio2"
                        checked={selectType === "na" ? true : false}
                        id={"score1"}
                        onChange={handleOptionChange}
                        value="na"
                        disabled={isViewMode}
                      />

                      <label className="form-check-label">N/A</label>
                    </div>
                    {selectType === "na" ? null : (
                      <div className="row mt-2  mb-2">
                        <div className="col-md-6 col-12">
                          <input
                            type="number"
                            className="form-control"
                            disabled={
                              selectType === "na" ||
                              quesType === 11 ||
                              isViewMode
                            }
                            value={selectType === "na" ? 0 : score}
                            minLength={0}
                            onChange={handleScoreChange}
                            onBlur={() => {
                              if (score === "" || score <= 0) {
                                setScore(1);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "-" || e.key === "e") {
                                e.preventDefault();
                              }
                            }}
                            min="1"
                            max="5"
                            required
                          />
                        </div>
                      </div>
                    )}
                  </Fragment>
                ) : null}
                <div className="row mt-4 mb-2">
                  <div className="col-12">
                    <label className="labeltext px-0">
                      {t("Question Title", { ns: "home" })}*
                    </label>
                    <br />
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-12">
                    <Editor
                      content={title}
                      type="title"
                      readOnly={isViewMode}
                      skipReset={true}
                    />
                    <small style={{ color: "red" }}>
                      {!qTitle && questionTitleError}
                    </small>
                  </div>
                </div>

                {Number(quesType) === 5 ? (
                  <Fragment>
                    <div className="row mt-4 mb-2">
                      <div className="col-12">
                        <label className="labeltext px-0">
                          Suggested Answer*
                        </label>
                      </div>
                    </div>
                    <div className="row   mb-2">
                      <div className="col-12">
                        <textarea
                          className="form-control"
                          rows={4}
                          value={suggestedAnswer}
                          onChange={(e) => setSuggestedAnswer(e.target.value)}
                          disabled={isViewMode}
                        />
                      </div>
                    </div>
                  </Fragment>
                ) : Number(quesType) === 14 ? (
                  <Fragment>
                    <div className="row mt-4 mb-2">
                      <div className="col-12">
                        <label className="labeltext px-0">File Upload*</label>
                      </div>
                    </div>

                    {!isViewMode && (
                      <div
                        className="dataset-row"
                        style={{ marginBottom: "10px" }}
                      >
                        <div className="dataset-label">
                          This File upload is used to show question.
                        </div>
                        <label className="upload-btn">
                          <img
                            src="/images/upload-icon-black.svg"
                            width={20}
                            alt=""
                          />
                          UPLOAD FILE
                          <input
                            type="file"
                            accept=".acc,.flac,.m4a,.mp3,.wav,.wma,.doc,.docx,.key,.keynote,.numbers,.odp,.ods,.odt,.pages,.pdf,.ppt,.pptx,.rtf,.txt,.xls,.xlsx,.csv,.ipynb,.gif,.jpeg,.jpg,.png,.avi,.m4v,.mov,.mp4,.mpg,.wmv,.zip"
                            style={{ display: "none" }}
                            onChange={handleFileUpload}
                          />
                        </label>
                      </div>
                    )}

                    <div className="row mt-4 mb-2 mx-0 my-0">
                      {uploadedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="file-chip col-md-6 col-12"
                          style={{ paddingLeft: "0" }}
                        >
                          <div
                            className="d-flex align-items-center justify-content-between mb-3"
                            style={{
                              background: "#9E9E9E3A",
                              padding: "8px",
                              borderRadius: "4px",
                            }}
                          >
                            <span className="file-name text-truncate">
                              {file.name}
                            </span>
                            <div className="file-actions">
                              {file.isUploading ? (
                                <div className="d-flex align-items-center gap-2 mb-2">
                                  <div
                                    className="spinner-border spinner-border-sm"
                                    style={{ color: "#F75891" }}
                                  />
                                  <span>Uploading...</span>
                                </div>
                              ) : (
                                <div className="d-flex gap-2">
                                  <img
                                    src="/images/download-pink.svg"
                                    alt="download"
                                    className="cursor-pointer"
                                    onClick={() =>
                                      downloadFile(file.url, file.name)
                                    }
                                    width={22}
                                  />
                                  {!isViewMode && (
                                    <img
                                      src="/images/delete-red.svg"
                                      alt="delete"
                                      className="cursor-pointer"
                                      onClick={() => removeFile(index)}
                                      width={20}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Fragment>
                ) : Number(quesType) === 6 ? (
                  <Fragment>
                    <div className="row mt-2 mb-2">
                      <div className="col-12">
                        <label className="labeltext px-0">
                          *{t("choices", { ns: "home" })}
                        </label>
                        <br />
                      </div>
                    </div>
                    <div className="row ">
                      <div className="col-12">
                        <table className="table table-hover right-ans-desc-main">
                          <tr>
                            <th
                              scope="col"
                              style={{
                                paddingBottom: 9,
                                textAlign: "center",
                              }}
                            >
                              {" "}
                              <label
                                className="table-th"
                                style={{
                                  color: "#F75891",
                                  background: "#fff",
                                  paddingBottom: 2,
                                }}
                              >
                                {t("Right Ans", { ns: "home" })}
                              </label>
                            </th>

                            <th scope="col" style={{ paddingBottom: 9 }}>
                              <label
                                className="table-th"
                                style={{
                                  color: "#F75891",
                                  background: "#fff",
                                  paddingBottom: 2,
                                }}
                              >
                                {t("Choice description", { ns: "home" })}
                              </label>
                              <br />
                            </th>
                          </tr>

                          {questionOptions &&
                            questionOptions.slice(0, 2)?.map((el, i) => {
                              return (
                                <tr>
                                  <td
                                    style={{
                                      width: "17%",
                                      textAlign: "center",
                                    }}
                                  >
                                    <input
                                      id="0"
                                      type="checkbox"
                                      name="0"
                                      value={el.index}
                                      checked={el.iscorrect ? true : false}
                                      isDisabled={isViewMode}
                                      className="form-check-input"
                                      onChange={(e) =>
                                        dispatch(
                                          setRightAnswer(
                                            i,
                                            el.index,
                                            el.iscorrect,
                                            quesType,
                                          ),
                                        )
                                      }
                                    />
                                  </td>

                                  <td>
                                    <div className="options-holder question-options-editor w-100 mt-3">
                                      <JoditEditor
                                        key={`opt6-${el.questionOptionID || el.index}-${optionsVersion}`}
                                        ref={editorRef}
                                        value={el.questionOption}
                                        config={config}
                                        tabIndex={1} // tabIndex of textarea
                                        onBlur={(newContent) =>
                                          onChangeOption(
                                            i,
                                            el.index,
                                            newContent,
                                          )
                                        } // preferred to use only this option to update the content for performance reasons
                                        onChange={(newContent) => { }}
                                        isDisabled={isViewMode}
                                      />
                                    </div>
                                    {
                                      <small
                                        style={{
                                          color: "red",
                                          fontWeight: "normal",
                                        }}
                                      >
                                        {el.optionError}
                                      </small>
                                    }
                                  </td>
                                </tr>
                              );
                            })}

                          <tr className="h-10">
                            <td colspan="3" style={{ lineHeight: 1 }}>
                              {" "}
                              &nbsp;
                            </td>
                          </tr>
                        </table>
                      </div>
                    </div>
                  </Fragment>
                ) : Number(quesType) === 11 ? (
                  <Fragment>
                    <div className="row mt-2 mb-2">
                      <div className="col-12">
                        <label className="labeltext px-0">
                          *{t("Question Options", { ns: "home" })}
                        </label>
                        <br />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12">
                        {[1, 2, 3, 4, 5].map((num, i) => (
                          <div
                            key={i}
                            className="d-flex align-items-center mb-3"
                          >
                            <span style={{ width: "30px" }}>{num}</span>
                            <input
                              type="text"
                              className="form-control"
                              value={questionOptions[i]?.questionOption || ""}
                              onChange={(e) =>
                                onChangeOption(
                                  i,
                                  questionOptions[i]?.index ?? i,
                                  e.target.value,
                                )
                              }
                              disabled={isViewMode}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Fragment>
                ) : (
                  <Fragment>
                    <div className="row mt-2 mb-2">
                      <div className="col-12">
                        <label className="labeltext px-0">
                          *{t("Question Options", { ns: "home" })}
                        </label>
                        <br />
                      </div>
                    </div>
                    <div className="row ">
                      <div className="col-12">
                        <table className="table table-hover right-ans-desc-main">
                          <tr>
                            <th
                              scope="col"
                              style={{
                                paddingBottom: 9,
                                textAlign: "center",
                              }}
                            >
                              {" "}
                              <label
                                className="table-th"
                                style={{
                                  color: "#F75891",
                                  background: "#fff",
                                  paddingBottom: 2,
                                }}
                              >
                                {t("Right Ans", { ns: "home" })}
                              </label>
                            </th>

                            <th scope="col" style={{ paddingBottom: 9 }}>
                              <label
                                className="table-th"
                                style={{
                                  color: "#F75891",
                                  background: "#fff",
                                  paddingBottom: 2,
                                }}
                              >
                                {t("Choice description", { ns: "home" })}
                              </label>
                              <br />
                            </th>
                          </tr>

                          {questionOptions &&
                            questionOptions
                              ?.slice(
                                0,
                                quesType === 1
                                  ? 4
                                  : quesType === 11
                                    ? 5
                                    : questionOptions.length,
                              )
                              .map((el, i) => {
                                return (
                                  <tr>
                                    <td
                                      style={{
                                        width: "17%",
                                        textAlign: "center",
                                      }}
                                    >
                                      {Number(quesType) === 2 ? (
                                        <input
                                          id="0"
                                          type="checkbox"
                                          name="0"
                                          value={el.index}
                                          className="form-check-input"
                                          onChange={(e) =>
                                            dispatch(
                                              setRightAnswer(
                                                i,
                                                el.index,
                                                el.iscorrect,
                                                quesType,
                                              ),
                                            )
                                          }
                                          isDisabled={isViewMode}
                                        />
                                      ) : (
                                        <input
                                          id="0"
                                          type="checkbox"
                                          name="0"
                                          value={el.index}
                                          checked={el.iscorrect ? true : false}
                                          className="form-check-input"
                                          onChange={(e) =>
                                            dispatch(
                                              setRightAnswer(
                                                i,
                                                el.index,
                                                el.iscorrect,
                                                quesType,
                                              ),
                                            )
                                          }
                                          isDisabled={isViewMode}
                                        />
                                      )}
                                    </td>
                                    <td>
                                      <div className="options-holder question-options-editor w-100 mt-3">
                                        <JoditEditor
                                          key={`opt-${el.questionOptionID || el.index}-${optionsVersion}`}
                                          ref={editorRef}
                                          value={el.questionOption}
                                          config={config}
                                          tabIndex={1} // tabIndex of textarea
                                          onBlur={(newContent) =>
                                            onChangeOption(
                                              i,
                                              el.index,
                                              newContent,
                                            )
                                          } // preferred to use only this option to update the content for performance reasons
                                          onChange={(newContent) => { }}
                                          isDisabled={isViewMode}
                                        />
                                      </div>
                                      {
                                        <small
                                          style={{
                                            color: "red",
                                            fontWeight: "normal",
                                          }}
                                        >
                                          {el.optionError}
                                        </small>
                                      }
                                    </td>
                                  </tr>
                                );
                              })}
                        </table>
                      </div>
                    </div>
                  </Fragment>
                )}
                {Number(quesType) === 1 && (
                  <div className="d-flex align-items-center gap-2">
                    <div className="form-check mt-3 mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={addComments}
                        onChange={(e) => setAddComments(e.target.checked)}
                        style={{ width: "16px", height: "16px" }}
                        isDisabled={isViewMode}
                      />
                      <label className="form-check-label">
                        Candidate needs to provide justification for the
                        selected choice
                      </label>
                    </div>
                  </div>
                )}

                {location.state.view ? null : (
                  <button
                    className={
                      window.innerWidth > 767
                        ? "btn-txt1 w-25"
                        : "btn-txt1 w-50"
                    }
                    onClick={saveQuestion}
                    disabled={saveLoading || fileUploading}
                    style={{
                      backgroundColor: saveLoading ? "#9CA3AF" : "",
                      cursor: saveLoading ? "not-allowed" : "pointer",
                      marginBottom: "40px",
                    }}
                  >
                    {saveLoading ? (
                      <div
                        className="loading-overlay"
                        style={{
                          alignItems: "start",
                          color: "#",
                          height: "auto",
                        }}
                      >
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden">
                            Loading job summary...
                          </span>
                        </div>
                      </div>
                    ) : (
                      <>
                        {saveLoading ? "Saving..." : t("Save", { ns: "home" })}
                      </>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Dialog
        className="screen-loader"
        open={saveLoading}
        onClose={() => { }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        Loading ...
      </Dialog>
    </div>
  );
}
